import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarIcon, ArrowRight, ArrowLeft, CheckCircle2, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

import { ymd as fmtDate } from "@/lib/date";

function monthLabel(d) {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function buildMonthGrid(viewDate) {
  const y = viewDate.getFullYear();
  const m = viewDate.getMonth();
  const first = new Date(y, m, 1);
  const startIdx = (first.getDay() + 6) % 7; // Mon=0
  const days = [];
  for (let i = 0; i < startIdx; i++) days.push(null);
  const last = new Date(y, m + 1, 0).getDate();
  for (let d = 1; d <= last; d++) days.push(new Date(y, m, d));
  return days;
}

export default function BookPage() {
  const ctx = useOutletContext();
  const settings = ctx?.settings;
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [consultationType, setConsultationType] = useState("");
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ patient_name: "", phone: "", email: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (settings?.consultation_types?.length && !consultationType) {
      setConsultationType(settings.consultation_types[0]);
    }
  }, [settings, consultationType]);

  const today = useMemo(() => new Date(new Date().toDateString()), []);
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 60);
    return d;
  }, []);
  const monthDays = useMemo(() => buildMonthGrid(viewDate), [viewDate]);

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    api
      .get(`/availability/slots`, { params: { date: selectedDate } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  const canNext1 = !!consultationType;
  const canNext2 = !!selectedDate && !!selectedSlot;
  const canSubmit = form.patient_name.trim().length >= 2 && /^[+0-9][0-9\s\-]{6,19}$/.test(form.phone.trim());

  async function submitBooking() {
    setSubmitting(true);
    try {
      const payload = {
        patient_name: form.patient_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        consultation_type: consultationType,
        date: selectedDate,
        start_time: selectedSlot.start_time,
        reason: form.reason.trim() || null,
      };
      const { data } = await api.post("/appointments/book", payload);
      setResult(data);
      setStep(5);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-2">Book an Appointment</div>
      <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">Reserve your slot</h1>
      <p className="text-slate-600 mt-2">
        With <span className="font-semibold">{settings?.doctor_name}</span> — {settings?.doctor_title}
      </p>

      <Stepper step={step} />

      <div className="mt-8 bg-white rounded-3xl border border-slate-200 p-6 md:p-10">
        {step === 1 && (
          <div data-testid="step-consultation">
            <h2 className="text-xl font-display font-semibold text-slate-900">1. Choose a consultation type</h2>
            <div className="mt-5 grid md:grid-cols-2 gap-4">
              {(settings?.consultation_types || []).map((t) => {
                const fee = (settings?.consultation_fees || []).find((f) => f.type === t);
                const active = consultationType === t;
                return (
                  <button
                    key={t}
                    onClick={() => setConsultationType(t)}
                    className={`text-left border rounded-2xl p-5 transition ${
                      active ? "border-emerald-500 bg-emerald-50/40 ring-2 ring-emerald-500/20" : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`booking-consultation-type-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <div className="font-display font-semibold text-slate-900">{t}</div>
                    {fee && <div className="text-sm text-emerald-700 font-semibold mt-1">{fee.currency === "INR" ? "₹" : fee.currency} {fee.amount}</div>}
                  </button>
                );
              })}
            </div>
            <StepNav onNext={() => setStep(2)} canNext={canNext1} />
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-datetime">
            <h2 className="text-xl font-display font-semibold text-slate-900">2. Pick a date &amp; time</h2>
            <div className="grid lg:grid-cols-2 gap-8 mt-5">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm"
                  >‹</button>
                  <div className="font-display font-semibold text-slate-800">{monthLabel(viewDate)}</div>
                  <button
                    onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm"
                  >›</button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-500 font-medium mb-2">
                  {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <div key={d}>{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1" data-testid="booking-date-picker">
                  {monthDays.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const iso = fmtDate(d);
                    const disabled = d < today || d > maxDate || (settings?.closed_weekdays || []).includes((d.getDay() + 6) % 7);
                    const active = iso === selectedDate;
                    return (
                      <button
                        key={i}
                        disabled={disabled}
                        onClick={() => setSelectedDate(iso)}
                        className={`aspect-square rounded-lg text-sm font-medium transition
                          ${active ? "bg-[#0F4C81] text-white" : disabled ? "text-slate-300" : "text-slate-700 hover:bg-emerald-50"}`}
                        data-testid={`date-${iso}`}
                      >
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="font-semibold text-slate-800 mb-3">Available slots</div>
                {!selectedDate && <div className="text-sm text-slate-500">Select a date to view slots.</div>}
                {selectedDate && loadingSlots && <div className="text-sm text-slate-500">Loading slots...</div>}
                {selectedDate && !loadingSlots && slots.length === 0 && (
                  <div className="text-sm text-slate-500">No slots available on this date.</div>
                )}
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1">
                  {slots.map((s) => {
                    const active = selectedSlot?.start_time === s.start_time;
                    return (
                      <button
                        key={s.start_time}
                        disabled={!s.available}
                        onClick={() => setSelectedSlot(s)}
                        className={`px-2 py-2 rounded-lg text-sm font-medium border transition
                          ${active ? "bg-emerald-600 text-white border-emerald-600" :
                            s.available ? "bg-white text-slate-700 border-slate-200 hover:border-emerald-400" :
                            "bg-slate-50 text-slate-400 border-slate-100 cursor-not-allowed line-through"}`}
                        data-testid={`booking-time-slot-${s.start_time}`}
                      >
                        {s.start_time}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <StepNav onBack={() => setStep(1)} onNext={() => setStep(3)} canNext={canNext2} />
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-details">
            <h2 className="text-xl font-display font-semibold text-slate-900">3. Your contact details</h2>
            <div className="grid md:grid-cols-2 gap-4 mt-5">
              <Field label="Full name" required>
                <input className="input" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} data-testid="booking-name-input" />
              </Field>
              <Field label="Mobile number" required>
                <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="booking-phone-input" />
              </Field>
              <Field label="Email (optional)"><input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email-input" /></Field>
              <Field label="Reason for visit (optional)">
                <input className="input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="booking-reason-input" />
              </Field>
            </div>
            <StepNav onBack={() => setStep(2)} onNext={() => setStep(4)} canNext={canSubmit} nextLabel="Review" />
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-review">
            <h2 className="text-xl font-display font-semibold text-slate-900">4. Review &amp; confirm</h2>
            <div className="mt-5 bg-slate-50 rounded-2xl p-5 space-y-2 text-sm">
              <Line k="Doctor" v={`${settings?.doctor_name}${settings?.doctor_title ? ` — ${settings.doctor_title}` : ""}`} />
              <Line k="Consultation" v={consultationType} />
              <Line k="Date" v={selectedDate} />
              <Line k="Time" v={selectedSlot?.start_time} />
              <Line k="Patient" v={form.patient_name} />
              <Line k="Phone" v={form.phone} />
              {form.email && <Line k="Email" v={form.email} />}
              {form.reason && <Line k="Reason" v={form.reason} />}
            </div>
            <p className="text-xs text-slate-500 mt-4">By submitting, you agree to our <Link to="/privacy" className="underline">Privacy</Link> and <Link to="/terms" className="underline">Terms</Link>.</p>
            <div className="flex items-center justify-between mt-6">
              <button onClick={() => setStep(3)} className="inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900 font-medium"><ArrowLeft size={16} /> Back</button>
              <button
                disabled={submitting}
                onClick={submitBooking}
                className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white font-semibold rounded-xl px-6 py-3"
                data-testid="booking-submit-button"
              >
                {submitting ? "Submitting..." : "Confirm booking"} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {step === 5 && result && (
          <div data-testid="step-confirmation" className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto"><CheckCircle2 size={32} /></div>
            <h2 className="text-2xl font-display font-bold text-slate-900 mt-4">
              {result.requires_approval ? "Booking request received" : "Appointment confirmed"}
            </h2>
            <p className="text-slate-600 mt-2 max-w-md mx-auto">
              {result.requires_approval ? "Your appointment is pending confirmation from the clinic." : "Your slot is reserved. Please arrive 10 minutes early."}
            </p>
            <div className="mt-6 inline-block bg-slate-50 rounded-2xl p-6 text-left">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Booking Reference</div>
              <div className="font-mono text-2xl font-bold text-[#0F4C81] mt-1" data-testid="booking-reference-display">{result.reference}</div>
              <div className="mt-4 text-sm space-y-1">
                <div><span className="text-slate-500">Status:</span> <StatusBadge status={result.status} /></div>
                <div><span className="text-slate-500">Date:</span> {result.date}</div>
                <div><span className="text-slate-500">Time:</span> {result.start_time} – {result.end_time}</div>
                <div><span className="text-slate-500">Type:</span> {result.consultation_type}</div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-center gap-3 text-sm">
              <Link to="/lookup" className="text-emerald-700 hover:text-emerald-800 font-medium">Check status later</Link>
              <span className="text-slate-300">·</span>
              <Link to="/" className="text-slate-600 hover:text-slate-800">Back to home</Link>
            </div>
          </div>
        )}
      </div>
      <style>{`.input{width:100%;padding:.65rem .9rem;border:1px solid #CBD5E1;border-radius:.75rem;background:#fff;color:#0F172A;font-size:.95rem;}
      .input:focus{outline:none;border-color:#0D9488;box-shadow:0 0 0 3px rgb(13 148 136 / 0.15)}`}</style>
    </section>
  );
}

function Stepper({ step }) {
  const items = ["Type", "Date & Time", "Details", "Review", "Done"];
  return (
    <div className="mt-6 flex items-center gap-2 text-xs flex-wrap">
      {items.map((label, i) => {
        const idx = i + 1;
        const done = idx < step;
        const active = idx === step;
        return (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full grid place-items-center font-semibold text-[11px]
              ${active ? "bg-[#0F4C81] text-white" : done ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>
              {idx}
            </div>
            <span className={`font-medium ${active ? "text-slate-900" : "text-slate-500"}`}>{label}</span>
            {idx < items.length && <span className="text-slate-300 mx-1">—</span>}
          </div>
        );
      })}
    </div>
  );
}

function StepNav({ onBack, onNext, canNext, nextLabel = "Continue" }) {
  return (
    <div className="flex items-center justify-between mt-7">
      {onBack ? (
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900 font-medium"><ArrowLeft size={16} /> Back</button>
      ) : <div />}
      <button
        onClick={onNext}
        disabled={!canNext}
        className="inline-flex items-center gap-2 bg-[#0F4C81] hover:bg-[#0A365C] disabled:opacity-40 text-white font-semibold rounded-xl px-5 py-2.5"
      >
        {nextLabel} <ArrowRight size={16} />
      </button>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-600 font-medium mb-1.5">{label}{required && <span className="text-rose-600"> *</span>}</div>
      {children}
    </label>
  );
}

function Line({ k, v }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-28 shrink-0 text-slate-500">{k}</div>
      <div className="font-medium text-slate-800">{v}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    confirmed: "bg-emerald-100 text-emerald-800",
    rescheduled: "bg-sky-100 text-sky-800",
    cancelled: "bg-rose-100 text-rose-800",
    completed: "bg-indigo-100 text-indigo-800",
    no_show: "bg-slate-200 text-slate-700",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] || "bg-slate-100 text-slate-700"}`} data-testid="appointment-status-badge">{status.replace("_", " ")}</span>;
}
